import type { SubscriptionPriceProduct, SubscriptionType, SubscriptionProductType, Subscription } from '@/models';
import type { User } from '@/models/user';
import type { FeatureConfig, FeatureConfigKey } from '@/models/feature-config';
import type { SubscriptionStatus } from '@/components/checkout/PricingCard';
import { getAdvertisedProductTypes } from '@/consts/pricing';

// Boolesche Features, die ein VERKÄUFLICHES Produkt per Definition gewährt — Basis für den
// "enthalten"-Vergleich gegen den echten user.featureConfig. Spiegelt backend GetFeatureConfig
// für die als Karten angebotenen Produkte. Ob ein Nutzer ein Feature tatsächlich besitzt, wird
// IMMER aus user.featureConfig gelesen (nie aus dem Pakettyp abgeleitet) — z. B. haben die
// Community-Pakete COMMUNITY und COMMUNITY_OLD kein Daytrading, COMMUNITY_BOOST dagegen schon.
// Die featureConfig ist die Vereinigung aller Abos (backend MergeWith, OR-Logik).
const PRODUCT_TYPE_FEATURES: Partial<Record<SubscriptionProductType, FeatureConfigKey[]>> = {
  LONGTERM:             ['longTermRecommendations'],
  SWINGTRADES_LONGTERM: ['longTermRecommendations', 'swingTradeRecommendations'],
  ALL:                  ['coreRecommendations', 'earningRecommendations', 'swingTradeRecommendations', 'longTermRecommendations'],
  DAYTRADING:           ['dayTrading'],
};

/** Laufendes, wiederkehrendes Campus-Abo. Einmalkäufe (Workshop, Ausbildung, FMB) bleiben
 *  im Backend dauerhaft ACTIVE und zählen hier bewusst nicht. */
export const hasActiveRecurringSubscription = (user: User | null): boolean =>
  user !== null &&
  user.activeSubscriptionProductTypes.some(pt => pt in PRODUCT_TYPE_FEATURES);

// Pakete, ab denen der Campus nichts Höheres mehr zu verkaufen hat — danach ist der nächste
// Schritt die Ausbildung / 100K Community (extern).
const COMMUNITY_UPGRADE_PRODUCT_TYPES: SubscriptionProductType[] = ['ALL', 'DAYTRADING'];

/**
 * Besitzt der Nutzer laut echter featureConfig bereits ALLE booleschen Features,
 * die diese Produktfamilie gewährt?
 */
export const isProductTypeCoveredByFeatures = (
  productType: SubscriptionProductType,
  featureConfig: FeatureConfig | null
): boolean => {
  if (featureConfig === null) return false;
  const required = PRODUCT_TYPE_FEATURES[productType];
  if (required === undefined || required.length === 0) return false;
  return required.every(flag => featureConfig[flag] === true);
};

/**
 * Produktfamilie bereits im Besitz — entweder als aktives Abo derselben Familie
 * oder weil alle ihre Features durch ein anderes Paket abgedeckt sind.
 */
export const isProductTypeOwned = (
  productType: SubscriptionProductType,
  featureConfig: FeatureConfig | null,
  activeProductTypes: SubscriptionProductType[]
): boolean =>
  activeProductTypes.includes(productType) || isProductTypeCoveredByFeatures(productType, featureConfig);

/**
 * true, wenn der Nutzer alle auf /pricing beworbenen Pakete bereits besitzt bzw.
 * deren Features abgedeckt sind — dann gibt es dort nichts mehr zu bewerben.
 *
 * Solange die Produkte noch nicht geladen sind, ist die Liste leer und das Ergebnis false —
 * so wird nichts vorschnell ausgeblendet.
 */
export const hasAllAdvertisedPackages = (user: User | null, products: SubscriptionPriceProduct[]): boolean => {
  if (user === null) return false;
  const advertisedProductTypes = getAdvertisedProductTypes(products);
  if (advertisedProductTypes.length === 0) return false;
  return advertisedProductTypes.every(productType =>
    isProductTypeOwned(productType, user.featureConfig, user.activeSubscriptionProductTypes)
  );
};

/**
 * true, wenn auf /pricing der nächste Schritt (Ausbildung / 100K Community) angeboten werden
 * soll: Der Nutzer ist im Cashflow- oder Daytrading-Paket bzw. hat bereits alle beworbenen
 * Pakete — und ist noch kein Community-Mitglied.
 */
export const shouldOfferCommunityUpgrade = (user: User | null, products: SubscriptionPriceProduct[]): boolean => {
  if (user === null) return false;
  if (user.featureConfig.communityMember) return false;

  const ownsAdvancedPackage = COMMUNITY_UPGRADE_PRODUCT_TYPES.some(productType =>
    isProductTypeOwned(productType, user.featureConfig, user.activeSubscriptionProductTypes)
  );

  return ownsAdvancedPackage || hasAllAdvertisedPackages(user, products);
};

/**
 * Was der Campus einem Community-Mitglied noch anbieten kann. Alles außer Boost und
 * Daytrading ist über die Community-Mitgliedschaft bereits abgedeckt — deshalb wird
 * /pricing für diese Gruppe auf genau diese beiden Angebote reduziert.
 *
 * Besitz wird ausschließlich aus der echten featureConfig gelesen: Boost ist als
 * COMMUNITY_BOOST kein Campus-Produkt und taucht daher nicht in PRODUCT_TYPE_FEATURES auf.
 *
 * Wichtig: COMMUNITY_BOOST gewährt laut backend GetFeatureConfig auch dayTrading, Boost ist
 * also eine Obermenge von Daytrading. Erreichbar sind damit nur diese Fälle:
 *   COMMUNITY_BOOST            → beides vorhanden, /pricing entfällt komplett
 *   COMMUNITY / COMMUNITY_OLD  → beide Karten (Daytrading als günstigerer Einstieg,
 *                                Boost als Premium-Pfad mit Bewerbung — so gewollt)
 *   COMMUNITY + DAYTRADING_*   → nur die Boost-Karte
 * showDaytrading ohne showBoost ist über Abos nicht erreichbar, sondern nur über einen
 * manuellen Admin-Override — wird hier aber sauber mitbehandelt.
 */
export interface CommunityUpsellState {
  isCommunityMember: boolean;
  showBoost: boolean;
  showDaytrading: boolean;
  hasAnyOffer: boolean;
}

export const getCommunityUpsellState = (user: User | null): CommunityUpsellState => {
  if (user === null || !user.featureConfig.communityMember) {
    return { isCommunityMember: false, showBoost: false, showDaytrading: false, hasAnyOffer: false };
  }

  const showBoost      = !user.featureConfig.boostRecommendations;
  const showDaytrading = !user.featureConfig.dayTrading;

  return {
    isCommunityMember: true,
    showBoost:         showBoost,
    showDaytrading:    showDaytrading,
    hasAnyOffer:       showBoost || showDaytrading,
  };
};

/**
 * Darf /pricing für diesen Nutzer überhaupt angezeigt werden?
 * Nicht-Community-Mitglieder: immer. Community-Mitglieder nur, solange Boost oder
 * Daytrading fehlt — sonst gibt es dort nichts mehr für sie.
 *
 * user === null (User-Context lädt noch) ergibt true, damit nicht vorschnell
 * weitergeleitet bzw. der Sidebar-Eintrag nicht kurz ausgeblendet wird.
 */
export const canSeePricingPage = (user: User | null): boolean => {
  const upsell = getCommunityUpsellState(user);
  return !upsell.isCommunityMember || upsell.hasAnyOffer;
};

export const getSubscriptionStatus = (
  product: SubscriptionPriceProduct,
  subscriptions: Subscription[],
  getProductByType: (type: SubscriptionType) => SubscriptionPriceProduct | undefined,
  featureConfig: FeatureConfig | null
): SubscriptionStatus => {

  // Only consider ACTIVE or PAST_DUE subscriptions
  const activeSubscriptions = subscriptions.filter(
    s => s.status === 'ACTIVE' || s.status === 'PAST_DUE'
  );

  const productBaseType = product.productType;

  if (productBaseType === null) return 'NOT_SUBSCRIBED';

  for (const sub of activeSubscriptions) {
    for (const item of sub.items) {
      const itemProduct  = getProductByType(item.type);
      const itemBaseType = itemProduct?.productType ?? null;
      const itemPeriod   = itemProduct?.intervalMonths ?? null;

      // Check for exact match
      if (item.type === product.type) return 'SUBSCRIBED';

      // Check for same base type with different period
      if (itemBaseType !== null && itemPeriod !== null && itemBaseType === productBaseType) {
        if (itemPeriod > product.intervalMonths) {
          return 'SUBSCRIBED_TO_HIGHER_PERIOD';
        } else if (itemPeriod < product.intervalMonths) {
          return 'SUBSCRIBED_TO_LOWER_PERIOD';
        }
      }
    }
  }

  // CONTAINED: the user already owns every (boolean) feature this product grants, through another
  // package (e.g. ALL contains Invest/Trade). Decisive is solely the real user.featureConfig —
  // e.g. Daytrading counts as contained only if featureConfig.dayTrading is actually true.
  if (isProductTypeCoveredByFeatures(productBaseType, featureConfig)) return 'CONTAINED';

  return 'NOT_SUBSCRIBED';
};
